import React, { useState, useEffect } from "react";
import { Table, Typography, Spin, Tag, Button, Card, Space, Modal, message, List, Divider, Input, Avatar } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import AffiliateUpdateModal from "./AffiliateUpdateModal";
import AffiliateUserListModal from "./AffiliateUserListModal";
import { PlusOutlined, EyeOutlined, EditOutlined, FacebookOutlined, InstagramOutlined, LinkedinOutlined, LinkOutlined, CopyOutlined, TwitterOutlined, SendOutlined, DollarOutlined, DeleteOutlined } from "@ant-design/icons";
import TelegramIcon from "../../../components/TelegramIcon";
import { ERROR_MESSAGE } from "../../../utils/constants";

const AffiliateUsers = () => {
    const [affiliateList, setAffiliateList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isUserListModalVisible, setIsUserListModalVisible] = useState(false);
    const [editingAffiliate, setEditingAffiliate] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedAffiliateId, setSelectedAffiliateId] = useState(null);
    const [affiliateUserList, setAffiliateUserList] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isLinksModalVisible, setIsLinksModalVisible] = useState(false);
    const [currentLinks, setCurrentLinks] = useState({});
    const [currentAffiliateLabel, setCurrentAffiliateLabel] = useState("");

    useEffect(() => {
        fetchAffiliateList();
    }, [pagination.current, pagination.pageSize]);

    const fetchAffiliateList = () => {
        setLoading(true);
        const { current, pageSize } = pagination;
        
        axios
            .get(`${process.env.REACT_APP_ADMIN_API_URL}affiliate/all?affiliateStatus=APPROVED&page=${current}&limit=${pageSize}`, {
                headers: {
                    "x-product-type": "PIP_GRID",
                },
            })
            .then((res) => {
                const responseData = res?.data?.payload || {};
                const data = responseData.data || [];
                const total = responseData.count || 0;
                
                const formattedData = data.map((item, idx) => ({
                    count: ((current - 1) * pageSize) + idx + 1,
                    key: item._id,
                    affiliateCode: item.affiliateCode,
                    commissionType: item.commissionType,
                    commissionValue: item.commissionValue,
                    affiliateTotalEarning: item.affiliateTotalEarning,
                    withdrawableCommission: item.withdrawableCommission,
                    blockedCommission: item.blockedCommission,
                    expiresAt: item.expiresAt ? dayjs(item.expiresAt).format("YYYY-MM-DD") : "",
                    createdAt: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD") : "",
                    status: item.affiliateStatus,
                    user: item.user,
                    socialMediaLinks: item.socialMediaLinks,
                }));
                
                setAffiliateList(formattedData);
                setPagination({
                    ...pagination,
                    total: total,
                });
                setLoading(false);
            })
            .catch((e) => {
                console.error("Error fetching affiliates:", e);
                setLoading(false);
            });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setPagination({
            ...pagination,
            current: pagination.current,
            pageSize: pagination.pageSize,
        });
    };

    const handleEdit = (record) => {
        setEditingAffiliate(record);
        setIsModalVisible(true);
    };

    const handleSubmit = async (values) => {
        setModalLoading(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_ADMIN_API_URL}affiliate/update/${editingAffiliate.affiliateCode}`,
                values,
                {
                    headers: {
                        "x-product-type": "PIP_GRID",
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("userToken")}`,
                    },
                }
            );
            setIsModalVisible(false);
            setEditingAffiliate(null);
            fetchAffiliateList();
        } catch (error) {
            // Optionally handle error
        } finally {
            setModalLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingAffiliate(null);
        setIsModalVisible(true);
    };

    const handleAddSubmit = async (values) => {
        setModalLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_ADMIN_API_URL}affiliate/add-affiliate`,
                { ...values, isAffiliate: true, productType: "PIP_GRID" },
                {
                    headers: {
                        "x-product-type": "PIP_GRID",
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("userToken")}`,
                    },
                }
            ).then(() => {
                setIsModalVisible(false);
                fetchAffiliateList();
            }).catch((error)=>{
                console.log(error)
                message.error(
                  ERROR_MESSAGE[error?.response.data.message] ||
                    error?.message ||
                    "Something went wrong"
                )
            });
        } catch (error) {
            // Optionally handle error
            console.error('Error adding affiliate:', error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleView = (userId) => {
        console.log("userId", userId);
        setSelectedAffiliateId(userId);
        setIsUserListModalVisible(true);
    };

    const openLinksModal = (record) => {
        setCurrentLinks(record.socialMediaLinks || {});
        const label = record.user?.fullName || record.affiliateCode || "Affiliate";
        setCurrentAffiliateLabel(label);
        setIsLinksModalVisible(true);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            message.success("Link copied to clipboard");
        } catch (e) {
            message.error("Failed to copy link");
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: 'Confirm Delete',
            content: `Are you sure you want to delete affiliate user "${record.user?.fullName || record.affiliateCode}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await axios.delete(
                        `${process.env.REACT_APP_ADMIN_API_URL}affiliate/delete/${record.affiliateCode}`,
                        {
                            headers: {
                                "x-product-type": "PIP_GRID",
                                "Authorization": `Bearer ${localStorage.getItem("userToken")}`,
                            },
                        }
                    );
                    message.success("Affiliate user deleted successfully");
                    fetchAffiliateList();
                } catch (error) {
                    console.error('Error deleting affiliate:', error);
                    message.error(
                        ERROR_MESSAGE[error?.response?.data?.message] ||
                        error?.message ||
                        "Failed to delete affiliate user"
                    );
                }
            },
        });
    };

    const getPlatformMeta = (key) => {
        const map = {
            facebook: { label: 'Facebook', color: '#1877F2', icon: <FacebookOutlined /> },
            instagram: { label: 'Instagram', color: '#E4405F', icon: <InstagramOutlined /> },
            linkedin: { label: 'LinkedIn', color: '#0A66C2', icon: <LinkedinOutlined /> },
            twitter: { label: 'Twitter', color: '#1DA1F2', icon: <TwitterOutlined /> },
            telegram: { label: 'Telegram', color: '#0088CC', icon: <TelegramIcon /> },
        };
        return map[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), color: '#595959', icon: <LinkOutlined /> };
    };

    const columns = [
        { title: "Sr. No", dataIndex: "count", key: "count", width: 50 },
        { title: "Name", dataIndex: ["user", "fullName"], key: "fullName", width: 200, render: (_, record) => record.user?.fullName || "-" },
        { title: "Email", dataIndex: ["user", "email"], key: "email", width: 220, render: (_, record) => record.user?.email || "-" },
        { title: "Phone Number", dataIndex: ["user", "phoneNumber"], key: "phoneNumber", width: 150, render: (_, record) => record.user?.phoneNumber || "-" },
        {
            title: "Social Media",
            key: "socialMedia",
            width: 150,
            render: (_, record) => {
                const hasLinks = Boolean(record.socialMediaLinks?.facebook || record.socialMediaLinks?.instagram || record.socialMediaLinks?.linkedin);
                return hasLinks ? (
                    <Button
                        type="default"
                        icon={<EyeOutlined />}
                        size={"middle"}
                        onClick={() => openLinksModal(record)}
                    />
                ) : (
                    <span>-</span>
                );
            },
        },
        { title: "Affiliate Code", dataIndex: "affiliateCode", key: "affiliateCode", width: 130 },
        { title: "Commission Type", dataIndex: "commissionType", key: "commissionType", width: 120, render: (text) => <Tag color={text === "percentage" ? "blue" : "green"}>{text}</Tag> },
        { title: "Commission Value", dataIndex: "commissionValue", key: "commissionValue", width: 120, render: (val, rec) => rec.commissionType === "percentage" ? `${val}%` : `$${val}` },
        { title: "Total Earning", dataIndex: "affiliateTotalEarning", key: "affiliateTotalEarning", width: 120, render: (val) => `$${val}` },
        // { title: "Withdrawable", dataIndex: "withdrawableCommission", key: "withdrawableCommission", width: 120, render: (val) => `$${val}` },
        // { title: "Blocked", dataIndex: "blockedCommission", key: "blockedCommission", width: 120, render: (val) => `$${val}` },
        { title: "Created At", dataIndex: "createdAt", key: "createdAt", width: 140 },
        { title: "Status", dataIndex: "status", key: "status", width: 120, render: (status) => (<Tag color={status === "APPROVED" ? "green" : status === "REJECTED" ? "red" : "orange"}>{status}</Tag>) },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, record) => {
                console.log("record", record);
                return (
                    <Space size="middle" style={{ gap: "8px" }}>
                        <Button
                            type="default"
                            icon={<DollarOutlined />}
                            size={"middle"}
                            onClick={() => handleView(record.user._id)}
                        />
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            size={"middle"}
                            onClick={() => handleEdit(record)}
                        />
                        <Button
                            type="default"
                            icon={<DeleteOutlined />}
                            size={"middle"}
                            onClick={() => handleDelete(record)}
                        />
                    </Space>
                )
            },
        },
    ];

    return (
        <div className="p-4">
            <Spin spinning={loading} size="large">
                <Card
                    title="Affiliate Users"
                    extra={
                        <Button type="primary" icon={<PlusOutlined />}
                            onClick={handleAddNew}
                        >
                            Add Affiliate User
                        </Button>
                    }>
                    <Table 
                        columns={columns} 
                        dataSource={affiliateList} 
                        rowKey="key" 
                        scroll={{ x: "max-content" }} 
                        pagination={{
                            ...pagination,
                        }}
                        onChange={handleTableChange}
                        loading={loading}
                    />
                    <Modal
                        title={`Social Media Links - ${currentAffiliateLabel}`}
                        open={isLinksModalVisible}
                        onCancel={() => setIsLinksModalVisible(false)}
                        footer={null}
                        width={640}
                    >
                        <div>
                            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                                Click Open to visit or Copy to share the link.
                            </Typography.Paragraph>
                            <Divider style={{ margin: '8px 0 16px' }} />
                            {Object.values(currentLinks || {}).filter(Boolean).length > 0 ? (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={Object.entries(currentLinks || {}).filter(([, url]) => Boolean(url))}
                                    renderItem={([platform, url]) => {
                                        const meta = getPlatformMeta(platform);
                                        return (
                                            <List.Item
                                                actions={[
                                                    <Button key="open" size="small" type="link" onClick={() => window.open(url, '_blank')}>Open</Button>,
                                                    <Button key="copy" icon={<CopyOutlined />} size="small" type="link" onClick={() => copyToClipboard(url)}>Copy</Button>,
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={<Avatar style={{ backgroundColor: meta.color }} icon={meta.icon} />}
                                                    title={<span style={{ fontWeight: 600 }}>{meta.label}</span>}
                                                    description={<Input size="small" value={url} readOnly style={{ padding: '8px' }} />}
                                                />
                                            </List.Item>
                                        );
                                    }}
                                />
                            ) : (
                                <Typography.Text type="secondary">No social media links available.</Typography.Text>
                            )}
                        </div>
                    </Modal>
                </Card>
                {isModalVisible && (
                    <AffiliateUpdateModal
                        editingAffiliate={editingAffiliate}
                        isModalVisible={isModalVisible}
                        setIsModalVisible={setIsModalVisible}
                        loading={modalLoading}
                        handleSubmit={editingAffiliate ? handleSubmit : handleAddSubmit}
                        title={editingAffiliate ? "Update Affiliate" : "Add New Affiliate"}
                    />
                )}
                {isUserListModalVisible && <AffiliateUserListModal
                    visible={isUserListModalVisible}
                    onClose={() => setIsUserListModalVisible(false)}
                    userId={selectedAffiliateId}
                />}
            </Spin>
        </div>
    );
};

export default AffiliateUsers;